import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskAPI, projectAPI, dashboardAPI } from '../services/api';
import useAuthStore from '../store/authStore';
import Layout from '../components/Layout';
import PunchInOutWidget from '../components/PunchInOutWidget';
import './OperationsHeadDashboard.css';

// Line Icons Component
const Icon = ({ name, size = 20 }) => {
  const icons = {
    briefcase: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
    checkCircle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
    clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    dollarSign: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
    alertCircle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    trendingUp: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
    activity: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
    target: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
    barChart: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>,
    pieChart: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></svg>,
    arrowUp: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>,
    arrowDown: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>,
  };
  return icons[name] || null;
};

const OperationsHeadDashboard = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalTasks: 0,
    inProgress: 0,
    completed: 0,
    totalTeamMembers: 0,
    developers: 0,
    projectManagers: 0,
    blocked: 0
  });
  const [projectHealth, setProjectHealth] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);



  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, projectsRes, activitiesRes] = await Promise.all([
        dashboardAPI.getOperationsHeadStats().catch(() => ({ data: { data: null } })),
        dashboardAPI.getProjectOverview().catch(() => ({ data: { data: [] } })),
        dashboardAPI.getRecentActivities().catch(() => ({ data: { data: [] } }))
      ]);
      
      if (statsRes.data.data) {
        setStats(statsRes.data.data);
      }
      
      if (projectsRes.data.data) {
        setProjectHealth(projectsRes.data.data.slice(0, 5));
      }
      
      if (activitiesRes.data.data) {
        setRecentActivities(activitiesRes.data.data.slice(0, 8));
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateProjectHealth = (project) => {
    const progress = parseFloat(project.progress) || 0;
    if (progress >= 80) return 'excellent';
    if (progress >= 60) return 'good';
    if (progress >= 40) return 'warning';
    return 'critical';
  };

  const getHealthColor = (health) => {
    const colors = {
      excellent: '#10B981',
      good: '#3B82F6',
      warning: '#F59E0B',
      critical: '#EF4444'
    };
    return colors[health] || '#6B7280';
  };



  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };





  if (loading) {
    return (
      <Layout>
        <div className="ops-dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="ops-dashboard">
        <div className="ops-header">
          <div className="ops-header-content">
            <div className="ops-header-left">
              <h1 className="ops-title">Operations Dashboard</h1>
              <p className="ops-subtitle">Real-time overview of all operations</p>
            </div>
          </div>
        </div>

        <PunchInOutWidget />

        <div className="ops-metrics-grid">
          <div className="metric-card primary">
            <div className="metric-icon">
              <Icon name="briefcase" size={24} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{stats.totalProjects || 0}</div>
              <div className="metric-label">Total Projects</div>
              <div className="metric-detail">
                <span className="detail-item active">{stats.activeProjects || 0} Active</span>
                <span className="detail-item completed">{stats.completedProjects || 0} Completed</span>
              </div>
            </div>
          </div>

          <div className="metric-card success">
            <div className="metric-icon">
              <Icon name="checkCircle" size={24} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{stats.completed || 0}</div>
              <div className="metric-label">Tasks Completed</div>
              <div className="metric-detail">
                <span className="detail-item">{stats.totalTasks || 0} Total Tasks</span>
              </div>
            </div>
          </div>

          <div className="metric-card warning">
            <div className="metric-icon">
              <Icon name="activity" size={24} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{stats.inProgress || 0}</div>
              <div className="metric-label">In Progress</div>
              <div className="metric-detail">
                <span className="detail-item danger">{stats.blocked || 0} Blocked</span>
              </div>
            </div>
          </div>

          <div className="metric-card info">
            <div className="metric-icon">
              <Icon name="users" size={24} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{stats.totalTeamMembers || 0}</div>
              <div className="metric-label">Team Members</div>
              <div className="metric-detail">
                <span className="detail-item">{stats.developers || 0} Devs</span>
                <span className="detail-item">{stats.projectManagers || 0} PMs</span>
              </div>
            </div>
          </div>
        </div>

        <div className="ops-content-grid">
          <div className="ops-section project-health-section">
            <div className="section-header">
              <div className="section-title">
                <Icon name="target" size={20} />
                <h2>Project Health</h2>
              </div>
              <button className="view-all-btn" onClick={() => navigate('/projects')}>View All</button>
            </div>
            <div className="project-health-list">
              {projectHealth.length === 0 ? (
                <div className="empty-state">
                  <Icon name="briefcase" size={32} />
                  <p>No projects available</p>
                </div>
              ) : (
                projectHealth.map(project => {
                  const health = calculateProjectHealth(project);
                  return (
                    <div key={project._id} className="project-health-item">
                      <div className="project-info">
                        <div className="project-name">{project.name}</div>
                        <div className="project-meta">
                          <span className="meta-item">
                            <Icon name="checkCircle" size={14} />
                            {project.completedTasks || 0}/{project.totalTasks || 0} Tasks
                          </span>
                          <span className="meta-item">
                            <Icon name="clock" size={14} />
                            {formatDate(project.endDate)}
                          </span>
                        </div>
                      </div>
                      <div className="project-progress">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ 
                              width: `${project.progress || 0}%`,
                              backgroundColor: getHealthColor(health)
                            }}
                          />
                        </div>
                        <span className="progress-text">{project.progress || 0}%</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="ops-section activity-section">
            <div className="section-header">
              <div className="section-title">
                <Icon name="activity" size={20} />
                <h2>Recent Activity</h2>
              </div>
            </div>
            <div className="activity-list">
              {recentActivities.length === 0 ? (
                <div className="empty-state">
                  <Icon name="activity" size={32} />
                  <p>No recent activities</p>
                </div>
              ) : (
                recentActivities.map((activity, index) => (
                  <div key={index} className="activity-item">
                    <div className="activity-icon">
                      <Icon name="checkCircle" size={16} />
                    </div>
                    <div className="activity-content">
                      <div className="activity-title">{activity.title}</div>
                      <div className="activity-meta">
                        <span>{activity.assignedTo || 'Unassigned'}</span>
                        <span className="separator">•</span>
                        <span>{activity.project || 'No Project'}</span>
                      </div>
                    </div>
                    <div className="activity-time">{getTimeAgo(activity.timestamp)}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OperationsHeadDashboard;
